<script lang="ts">
  import { tStore } from '../i18n'

  interface Props {
    text: string
    collapsedLines?: number
    maxLines?: number
  }

  /*
   * Collapse/expand only kicks in once the text renders to more than
   * `maxLines` lines at the current width; short text is shown in full with
   * no controls. Line counts depend on the rendered width and font, so they
   * are measured via -webkit-line-clamp rather than guessed from character
   * count.
   */
  let { text, collapsedLines = 3, maxLines = 7 }: Props = $props()

  let expanded = $state(false)
  let overflowing = $state(false)
  let contentEl: HTMLElement | undefined = $state()

  function measure() {
    if (!contentEl) return
    overflowing = contentEl.scrollHeight - contentEl.clientHeight > 1
  }

  $effect(() => {
    void text
    expanded = false

    if (!contentEl) return
    measure()

    if (typeof ResizeObserver === 'undefined') return
    /*
     * Guarded here, not inside measure(): expanding removes the clamp, which
     * changes the element's height and fires this callback with an unclamped
     * box (scrollHeight == clientHeight) — remeasuring then would wrongly
     * conclude the text no longer overflows and hide the controls. Reading
     * `expanded` inside the effect body itself (e.g. inside measure()) would
     * instead make it a dependency of this effect, which also *sets*
     * `expanded` — an self-defeating loop that reverts every toggle click.
     */
    const observer = new ResizeObserver(() => {
      if (!expanded) measure()
    })
    observer.observe(contentEl)
    return () => observer.disconnect()
  })

  let clampLines = $derived(overflowing ? collapsedLines : maxLines)
</script>

<div class="expandable-text">
  <p
    class="expandable-text__content"
    class:expandable-text__content--clamped={!expanded}
    style={!expanded ? `-webkit-line-clamp: ${clampLines}; line-clamp: ${clampLines};` : ''}
    bind:this={contentEl}
  >
    {text}
  </p>

  {#if overflowing}
    <button
      type="button"
      class="expandable-text__toggle"
      onclick={() => (expanded = !expanded)}
      aria-expanded={expanded}
    >
      {expanded
        ? $tStore('views.procurementDetail.showLess')
        : $tStore('views.procurementDetail.showMore')}
    </button>
  {/if}
</div>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .expandable-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .expandable-text__content {
    margin: 0;
    white-space: pre-wrap;

    &--clamped {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .expandable-text__toggle {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    padding: v.$space-xs 0;
    background: none;
    border: none;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    color: v.$color-primary;

    &:hover {
      color: v.$color-primary-hover;
    }

    &:focus-visible {
      outline: 2px solid v.$color-primary;
      outline-offset: 2px;
      border-radius: v.$radius-sm;
    }
  }
</style>
